import React, { useRef, useState } from "react";
import Cookies from "js-cookie";
import Modal from "frontend/components/Modal";
import useField from "frontend/utils/useField";
import { setSession } from "frontend/utils/authenticateHelper.js";
import { CLIENT_ID } from "frontend/utils/constants.js";
import "./SignupModal.scss";

const EMAIL_LIKE_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Claiming a guest reuses the existing token, so it skips the /oauth/token
// round trip that a fresh signup needs.
const claimGuestAccount = async ({ username, password, email }) => {
  const response = await fetch("/api/v1/guest/claim", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${Cookies.get("access_token")}`
    },
    body: JSON.stringify({ user: { username, password, email } })
  });

  const json = await response.json();

  if (!response.ok) {
    throw new Error((json.errors || ["Could not save your account."]).join(". "));
  }

  setSession(Cookies.get("access_token"), username);
};

const SignupModal = ({ isGuest, onClose, onLogin, onSwitchToLogin }) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const username = useField("");
  const password = useField("");
  const email = useField("");
  const usernameInputRef = useRef(null);

  const handleUsernameChange = (e) => {
    username.handleChange(e);
    usernameInputRef.current.setCustomValidity(
      EMAIL_LIKE_PATTERN.test(e.target.value) ? "Username can't be an email address." : ""
    );
  };

  const loginAfterSignup = async (usernameValue, passwordValue) => {
    try {
      const response = await fetch("/oauth/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: new URLSearchParams({
          grant_type: "password",
          username: usernameValue,
          password: passwordValue,
          client_id: CLIENT_ID
        })
      });

      const json = await response.json();

      if (response.status === 200) {
        setSession(json.access_token, usernameValue);
        onLogin();
        onClose();
      } else {
        // Account created but login failed - switch to login modal
        onSwitchToLogin();
      }
    } catch (error) {
      console.error("Auto-login error:", error);
      onSwitchToLogin();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    try {
      if (isGuest) {
        await claimGuestAccount({
          username: username.value,
          password: password.value,
          email: email.value
        });
        onLogin();
        onClose();
        return;
      }

      const response = await fetch(
        `/api/v1/users?user[username]=${username.value}&user[password]=${password.value}&user[email]=${email.value}`,
        { method: "POST" }
      );

      const json = await response.json();

      if (response.status === 200) {
        // Auto-login after successful signup
        await loginAfterSignup(username.value, password.value);
      } else {
        // Format error message from API response
        if (json.errors) {
          const errorMessages = Object.entries(json.errors)
            .map(([field, msgs]) => `${field} ${msgs.join(", ")}`)
            .join(". ");
          setErrorMessage(errorMessages);
        } else {
          setErrorMessage("Failed to create account. Please try again.");
        }
      }
    } catch (error) {
      console.error("Signup error:", error);
      // A rejected claim carries a real reason (e.g. username taken); a network
      // failure does not, so only the former is worth showing verbatim.
      setErrorMessage(
        isGuest && error.message ? error.message : "Something went wrong. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal title={isGuest ? "Save your progress" : "Sign Up"} onClose={onClose} maxWidth="360px">
      <div className="signup-modal-content">
        <form onSubmit={handleSubmit}>
          <div className="form-field">
            <label htmlFor="signup-username">Username</label>
            <input
              id="signup-username"
              ref={usernameInputRef}
              type="text"
              placeholder="Choose a username"
              value={username.value}
              onChange={handleUsernameChange}
              required
              autoCapitalize="none"
              autoFocus
              maxLength={15}
              minLength={2}
            />
          </div>
          <div className="form-field">
            <label htmlFor="signup-password">Password</label>
            <input
              id="signup-password"
              type="password"
              placeholder="Choose a password"
              value={password.value}
              onChange={password.handleChange}
              required
              minLength={6}
            />
          </div>
          <div className="form-field">
            <label htmlFor="signup-email">Email (optional)</label>
            <input
              id="signup-email"
              type="email"
              placeholder="Enter your email"
              value={email.value}
              onChange={email.handleChange}
            />
          </div>
          {errorMessage && (
            <p className="error-message">{errorMessage}</p>
          )}
          <button type="submit" className="submit-btn" disabled={isLoading}>
            {isLoading ? "Creating account..." : isGuest ? "Create my account" : "Create Account"}
          </button>
        </form>
        {isGuest ? (
          <>
            <p className="signup-guest-note">
              Your current games stay exactly as they are.
            </p>
            {/* Logging in abandons the guest row, so say so -- claiming keeps
                the games, logging in does not. */}
            <div className="login-prompt">
              Already have an account?{" "}
              <button type="button" className="link-btn" onClick={onSwitchToLogin}>
                Log in instead
              </button>
              {" "}&mdash; guest games won&rsquo;t carry over.
            </div>
          </>
        ) : (
          <div className="login-prompt">
            Already have an account?{" "}
            <button type="button" className="link-btn" onClick={onSwitchToLogin}>
              Log in here
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default React.memo(SignupModal);
